import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Trading hours filter constants
export const HOURS_FILTER = {
  all: "all",
  trading: "trading",
};

// Trading hours configuration
export const TRADING_HOURS_CONFIG = {
  timezone: "America/Chicago",
  startHour: 8,
  startMinute: 30,
  endHour: 15,
  endMinute: 0,
  weekendDays: [0, 6], // Sunday and Saturday
};

/**
 * Check if a timestamp falls within trading hours (8:30 AM - 3:00 PM CT, Monday-Friday)
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {boolean} - true if within trading hours, false otherwise
 */
export function isWithinTradingHours(timestamp) {
  const dt = dayjs(timestamp).tz(TRADING_HOURS_CONFIG.timezone);
  const hour = dt.hour();
  const minute = dt.minute();
  const dayOfWeek = dt.day(); // 0=Sunday, 6=Saturday

  // Monday-Friday only
  if (TRADING_HOURS_CONFIG.weekendDays.includes(dayOfWeek)) return false;

  // Check if within trading hours
  const timeInMinutes = hour * 60 + minute;
  const startMinutes = TRADING_HOURS_CONFIG.startHour * 60 + TRADING_HOURS_CONFIG.startMinute;
  const endMinutes = TRADING_HOURS_CONFIG.endHour * 60 + TRADING_HOURS_CONFIG.endMinute;

  return timeInMinutes >= startMinutes && timeInMinutes <= endMinutes;
}

/**
 * Apply trading hours filtering to cumulative PNL data (client-side)
 * @param {Array} cumulatedPNLList - Array of series objects with data points
 * @param {string} hoursFilter - 'all' or 'trading'
 * @returns {Array} - Filtered series list
 */
export function applyTradingHoursFilterToSeriesList(cumulatedPNLList, hoursFilter) {
  if (hoursFilter === HOURS_FILTER.all) {
    // No filtering needed, return as-is
    return cumulatedPNLList;
  }

  // Trading hours mode: recalculate cumulative PNL with non-trading hours set to 1.0 return
  return cumulatedPNLList.map((series) => {
    const filteredData = [];
    let cumulativeValue = 1.0;

    series.data.forEach((point, index) => {
      const timestamp = point.x;
      const isTrading = isWithinTradingHours(timestamp);

      if (point.y === undefined) {
        // No data for this point, keep cumulative value unchanged
        filteredData.push({
          x: point.x,
          y: cumulativeValue,
          isDeactivated: point.isDeactivated,
          color: point.color,
        });
      } else {
        // Calculate the return for this period
        const periodReturn = index > 0 ? point.y / series.data[index - 1].y : point.y;

        // Apply filter: use real return during trading hours, 1.0 otherwise
        const adjustedReturn = isTrading ? periodReturn : 1.0;
        cumulativeValue *= adjustedReturn;

        filteredData.push({
          x: point.x,
          y: cumulativeValue,
          isDeactivated: point.isDeactivated,
          color: point.color,
        });
      }
    });

    // Return new series object with filtered data
    return { ...series, data: filteredData };
  });
}
